/**
 * Intense W — order attribution capture (consent-aware), port of vitgem-attribution.js
 *
 * Stores the visitor's first touch (landing path, referrer, UTM, gclid) and the
 * GA4 client_id/session_id, then writes them into cart attributes so they land in
 * the order's "Additional details" in admin:
 *
 *   _iw_first   landing|source/medium|campaign   (first touch, never overwritten)
 *   _iw_gclid   Google Ads click id, when present
 *   _iw_ga_cid  GA4 client_id  (from the _ga cookie)
 *   _iw_ga_sid  GA4 session_id (from the _ga_<stream> cookie)
 *
 * GA4 runs inside the Google & YouTube app pixel, so the ids are read from the
 * first-party cookies it sets; gtag('get') is only a fallback.
 *
 * GDPR: without Shopify analytics consent nothing touches localStorage — the visit
 * lives in sessionStorage only and GA ids are not sent.
 */
(function () {
  'use strict';

  var GA4_ID = 'G-HCJQ9CB1SG';

  function consentAllowed() {
    try {
      var cp = window.Shopify && window.Shopify.customerPrivacy;
      if (cp && typeof cp.analyticsProcessingAllowed === 'function') return !!cp.analyticsProcessingAllowed();
    } catch (e) {}
    return false;
  }

  function store() {
    return consentAllowed() ? window.localStorage : window.sessionStorage;
  }

  function cookie(name) {
    var m = document.cookie.match(new RegExp('(?:^|;\\s*)' + name + '=([^;]*)'));
    return m ? decodeURIComponent(m[1]) : '';
  }

  // ── first touch ───────────────────────────────────────────────────────────
  function captureTouch() {
    var params = new URLSearchParams(window.location.search);
    var touch = {
      landing: window.location.pathname,
      referrer: document.referrer || '',
      utm_source: params.get('utm_source') || '',
      utm_medium: params.get('utm_medium') || '',
      utm_campaign: params.get('utm_campaign') || '',
      utm_term: params.get('utm_term') || '',
      utm_content: params.get('utm_content') || '',
      gclid: params.get('gclid') || '',
      ts: new Date().toISOString()
    };
    // internal navigation is not a touch
    if (touch.referrer && !touch.utm_source && !touch.gclid) {
      try { if (new URL(touch.referrer).hostname === window.location.hostname) touch.referrer = ''; } catch (e) {}
    }
    try {
      var s = store();
      // consent granted mid-visit: carry the session's first touch over, don't replace it
      if (!s.getItem('iw_attr_first')) s.setItem('iw_attr_first', JSON.stringify(readTouch('iw_attr_first') || touch));
      // last touch only matters when it carries campaign data
      if (touch.gclid || touch.utm_source) s.setItem('iw_attr_last', JSON.stringify(touch));
    } catch (e) {}
    return touch;
  }

  // Read from both stores: consent can flip mid-visit, which moves where we write.
  function readTouch(key) {
    for (var i = 0; i < 2; i++) {
      try {
        var raw = (i ? window.sessionStorage : window.localStorage).getItem(key);
        if (raw) return JSON.parse(raw);
      } catch (e) {}
    }
    return null;
  }

  // _ga = GA1.1.<cid>; _ga_<id> = GS2.1.s<sid>$o1... (or legacy GS1.1.<sid>.1...)
  function gaIdsFromCookies() {
    var ids = {};
    var ga = cookie('_ga').split('.');
    if (ga.length >= 4) ids.cid = ga.slice(-2).join('.');
    var gs = cookie('_ga_' + GA4_ID.replace(/^G-/, ''));
    var m = gs.match(/^GS2\.\d+\.s(\d+)/) || gs.match(/^GS1\.\d+\.(\d+)/);
    if (m) ids.sid = m[1];
    return ids;
  }

  // ── cart attribute sync ───────────────────────────────────────────────────
  function buildAttributes(cb) {
    var first = readTouch('iw_attr_first') || captureTouch();
    var last = readTouch('iw_attr_last');
    var attrs = {
      _iw_first: [first.landing, (first.utm_source || sourceFromReferrer(first.referrer)) + '/' + (first.utm_medium || ''), first.utm_campaign].join('|')
    };
    var gclid = (last && last.gclid) || first.gclid;
    if (gclid) attrs._iw_gclid = gclid;

    if (!consentAllowed()) return cb(attrs);

    var ids = gaIdsFromCookies();
    if (ids.cid) attrs._iw_ga_cid = ids.cid;
    if (ids.sid) attrs._iw_ga_sid = ids.sid;
    if ((ids.cid && ids.sid) || typeof window.gtag !== 'function') return cb(attrs);

    // gtag 'get' callbacks never fire if the stream isn't configured on the page,
    // so cap the wait and move on.
    var pending = 2;
    var done = false;
    var finish = function () { if (!done) { done = true; cb(attrs); } };
    window.gtag('get', GA4_ID, 'client_id', function (id) {
      if (id && !attrs._iw_ga_cid) attrs._iw_ga_cid = String(id);
      if (--pending === 0) finish();
    });
    window.gtag('get', GA4_ID, 'session_id', function (id) {
      if (id && !attrs._iw_ga_sid) attrs._iw_ga_sid = String(id);
      if (--pending === 0) finish();
    });
    setTimeout(finish, 2000);
  }

  function sourceFromReferrer(ref) {
    if (!ref) return 'direct';
    try { return new URL(ref).hostname.replace(/^www\./, ''); } catch (e) { return ''; }
  }

  function hasCart() {
    return /(^|;\s*)cart=/.test(document.cookie);
  }

  var syncing = false;
  function syncCartAttributes() {
    if (syncing) return;
    syncing = true;
    buildAttributes(function (attrs) {
      // cart token in the stamp: a fresh cart after an order gets synced again
      var stamp = cookie('cart') + JSON.stringify(attrs);
      try {
        if (store().getItem('iw_attr_synced') === stamp) { syncing = false; return; }
      } catch (e) {}
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs })
      }).then(function (r) {
        if (r.ok) { try { store().setItem('iw_attr_synced', stamp); } catch (e) {} }
      }).catch(function () {}).finally(function () { syncing = false; });
    });
  }

  captureTouch();

  // A cart exists once Shopify sets the `cart` cookie — sync on those pages so
  // attributes survive even when the visitor added to cart on a page we missed.
  if (hasCart()) setTimeout(syncCartAttributes, 1500);

  // First add-to-cart creates the cart; give the add request a moment to land.
  document.addEventListener('submit', function (e) {
    if (e.target && e.target.matches && e.target.matches('form[data-type="add-to-cart-form"], form[action*="/cart/add"]')) {
      setTimeout(syncCartAttributes, 1500);
    }
  }, true);
  // Horizon fires cart:update after AJAX add (product form, quick add)
  document.addEventListener('cart:update', function () { setTimeout(syncCartAttributes, 500); });

  // Consent granted mid-visit → GA ids may now be sent.
  document.addEventListener('visitorConsentCollected', function () {
    try { store().removeItem('iw_attr_synced'); } catch (e) {}
    if (hasCart()) setTimeout(syncCartAttributes, 1500);
  });
})();
